import { useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import BrandsTab from '../components/domain/BrandsTab';
import TargetGroupsTab from '../components/domain/TargetGroupsTab';
import DomainDetailsForm from '../components/domain/DomainDetailsForm';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { domainService } from '../services/domainService';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import Tabs from '../components/shared/Tabs';

interface DomainData {
  domain: string;
  modify_comment: string | null;
}

const DomainDetails = () => {
  const { companyId, domainId } = useParams<{ companyId: string; domainId: string }>();
  const { domains, companies, fetchRelatedDomainData, fetchDomains } = useData();
  const domain = domains.find((d) => d.id === domainId);
  const company = companies.find((c) => c.id === companyId);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('details');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (domainId) {
      fetchRelatedDomainData(domainId);
    }
  }, [companyId, domainId]);

  // Add this array to define the tabs
  const tabs = [
    { id: 'details', label: t('domain.details') },
    { id: 'brands', label: t('domain.brands') },
    { id: 'targetGroups', label: t('domain.target_groups') },
  ];

  if (!domain) {
    return <div>{t('domain.domain_not_found')}</div>;
  }

  const handleDomainSubmit = async (domainData: DomainData) => {
    try {
      if (!domainId || !companyId) {
        throw new Error(t('domain.missing_ids'));
      }

      const data = {
        ...domainData,
        company_id: companyId,
      };
      await domainService.updateDomain(domainId, data);
      showNotification(t('domain.domain_saved'), 'success');
      setIsModalOpen(false);
      fetchDomains(companyId);
    } catch (error) {
      console.error('Error saving domain:', error);
      showNotification(t('domain.error_saving_domain'), 'error');
    }
  };

  const renderTabContent = () => {
    if (!domainId) return null;

    switch (activeTab) {
      case 'brands':
        return <BrandsTab />;
      case 'targetGroups':
        return <TargetGroupsTab domainId={domainId} />;
      case 'details':
        return (
          <DomainDetailsForm
            domain={domain}
            companyId={companyId || ''}
            onClose={() => navigate(-1)}
            onSubmit={handleDomainSubmit}
          />
        );
      default:
        return (
          <DomainDetailsForm
            domain={domain}
            companyId={companyId || ''}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleDomainSubmit}
          />
        );
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            { path: '/companies', label: t('company.companies') },
            { label: company?.name || '', path: `/company/${companyId}#domains` },
            { label: t('domain.domains') },
            { label: domain?.domain },
          ]}
        />

        <button
          onClick={() => navigate(-1)}
          className="px-2 py-1 text-sm border border-gray-500 text-gray-500 bg-white rounded hover:bg-gray-100"
        >
          {t('common.back')}
        </button>
      </div>
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} className="mb-3" />

      <div
        className="mt-4 p-4 bg-white shadow-md rounded-sm"
        style={{ height: 'calc(100vh - 155px)' }}
      >
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DomainDetails;
